import { Button } from "@ascleo/ui/components/ui/button";
import { eligibleContactKind } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { maskValue } from "./masking";

/**
 * The reveal toggle for one contact: a labelled "Anzeigen" / "Verbergen" button (KC-65).
 *
 * Reveal is per contact rather than per category: on a shared terminal, showing every number at
 * once to read one of them is the disclosure the masking exists to prevent. The step hides a
 * revealed value again on its own after a short time (KC-56).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <RevealButton revealed={false} onToggle={() => {}} />
 * ```
 */
export function RevealButton({
  revealed,
  onToggle,
}: {
  revealed: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Icon = revealed ? EyeOff : Eye;
  return (
    <Button
      type="button"
      variant="outline"
      size="2xl"
      onClick={onToggle}
      aria-pressed={revealed}
      data-testid="patient-info-contact-reveal-button"
    >
      <Icon aria-hidden className="size-6" />
      {t(
        revealed
          ? "self-service.check-in.patient-info.review.hide"
          : "self-service.check-in.patient-info.review.reveal",
      )}
    </Button>
  );
}

/**
 * A contact's value as the resting check sheet prints it.
 *
 * Phone numbers use tabular figures so a masked value and its revealed form occupy the same width
 * and the row does not jump when the patient reveals it.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ContactValue contact={contact} revealed={false} />
 * ```
 */
export function ContactValue({
  contact,
  revealed,
}: {
  contact: ReviewContact;
  revealed: boolean;
}) {
  return (
    <span
      className={`text-xl leading-tight break-all ${
        eligibleContactKind(contact.type) === "phone" ? "tabular-nums" : ""
      }`}
      data-testid="patient-info-contact-value"
    >
      {revealed ? contact.value : maskValue(contact.value)}
    </span>
  );
}

/**
 * An additional (non-primary) contact, shown once the category is expanded.
 *
 * A protected entry gets the reception chip in place of the reveal control: its handling differs
 * from an ordinary contact's, and that difference has to be visible before the patient tries to
 * change it. The protection *reason* is never rendered — every protected contact looks the same to
 * the patient.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <AdditionalContactRow contact={contact} revealed={false} onToggleReveal={() => {}} />
 * ```
 */
export function AdditionalContactRow({
  contact,
  revealed,
  onToggleReveal,
}: {
  contact: ReviewContact;
  revealed: boolean;
  onToggleReveal: () => void;
}) {
  const { t } = useTranslation();
  const isProtected = contact.protection !== undefined;

  return (
    <li
      className="flex min-w-0 flex-col gap-2 border-t py-3 md:flex-row md:items-center md:gap-4"
      data-testid="patient-info-additional-contact"
    >
      <span className="text-base text-foreground md:w-[190px] md:shrink-0">
        {t(`self-service.check-in.patient-info.review.type.${contact.type}`)}
      </span>
      <ContactValue contact={contact} revealed={revealed && !isProtected} />
      <span className="md:ml-auto">
        {isProtected ? (
          <ProtectedChip />
        ) : (
          <RevealButton revealed={revealed} onToggle={onToggleReveal} />
        )}
      </span>
    </li>
  );
}

/**
 * The chip on a protected contact: its changes go to the reception, worded as a fact rather than a
 * workflow state (KC-54), in kiosk text size (KC-09).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <ProtectedChip />
 * ```
 */
export function ProtectedChip() {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex min-h-8 items-center gap-2 rounded-full bg-brand-supporting-subtle px-3 py-1 text-base font-medium text-brand-supporting-ink"
      data-testid="patient-info-protected-chip"
    >
      <ShieldAlert className="size-4" aria-hidden="true" />
      {t("self-service.check-in.patient-info.review.chip-reception-v2")}
    </span>
  );
}
